import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('patient');
  const [errorMessage, setErrorMessage] = useState('');
  const { register, loading, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    try {
      await register(email, password, role);
      navigate('/login');
    } catch (err) {
      // Error is already handled by AuthContext
      // Just log it for debugging
      console.error('Registration error:', err);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-layout">
        {/* Image Section */}
        <div className="auth-image">
          <div className="auth-image-content">
            <h2>Join NeutrinoRx</h2>
            <p>
              Create an account to access our intelligent healthcare platform.
              Streamline your prior authorization process and reduce administrative burden with the most advanced authorization solution.
            </p>
            
            {/* SVG Illustration */}
            <div className="auth-image-illustration">
              <svg width="100%" height="100%" viewBox="0 0 500 400" xmlns="http://www.w3.org/2000/svg">
                {/* Background Elements */}
                <circle cx="250" cy="200" r="180" fill="#ebf8ff" opacity="0.5" />
                <circle cx="250" cy="200" r="150" fill="#bee3f8" opacity="0.3" />
                
                {/* Medical Document with Shadow */}
                <rect x="150" y="100" width="200" height="250" fill="#ffffff" rx="10" filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.1))" />
                <rect x="150" y="100" width="200" height="30" fill="#4299e1" rx="10 10 0 0" />
                <circle cx="165" cy="115" r="5" fill="#ffffff" />
                <circle cx="180" cy="115" r="5" fill="#ffffff" />
                <circle cx="195" cy="115" r="5" fill="#ffffff" />
                
                {/* Document Lines */}
                <line x1="180" y1="160" x2="320" y2="160" stroke="#bee3f8" strokeWidth="6" strokeLinecap="round" />
                <line x1="180" y1="190" x2="320" y2="190" stroke="#bee3f8" strokeWidth="6" strokeLinecap="round" />
                <line x1="180" y1="220" x2="320" y2="220" stroke="#bee3f8" strokeWidth="6" strokeLinecap="round" />
                <line x1="180" y1="250" x2="270" y2="250" stroke="#bee3f8" strokeWidth="6" strokeLinecap="round" />
                
                {/* Checkmark in Circle */}
                <circle cx="250" cy="300" r="30" fill="#4299e1" />
                <path d="M235,300 L245,310 L265,290" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                
                {/* Stethoscope */}
                <circle cx="100" cy="150" r="15" fill="#ffffff" stroke="#4299e1" strokeWidth="4" />
                <path d="M100,165 C100,165 100,220 150,220" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" fill="none" />
                <path d="M100,165 C100,165 100,220 50,220" stroke="#ffffff" strokeWidth="6" strokeLinecap="round" fill="none" />
                <path d="M100,165 C100,165 100,220 150,220" stroke="#4299e1" strokeWidth="4" strokeLinecap="round" fill="none" />
                <path d="M100,165 C100,165 100,220 50,220" stroke="#4299e1" strokeWidth="4" strokeLinecap="round" fill="none" />
                
                {/* Medical Cross */}
                <circle cx="400" cy="150" r="25" fill="#ffffff" stroke="#4299e1" strokeWidth="4" />
                <rect x="395" y="125" width="10" height="50" fill="#4299e1" rx="5" />
                <rect x="375" y="145" width="50" height="10" fill="#4299e1" rx="5" />
                
                {/* Decorative Elements */}
                <circle cx="80" cy="80" r="10" fill="#ffffff" />
                <circle cx="420" cy="80" r="10" fill="#ffffff" />
                <circle cx="80" cy="320" r="10" fill="#ffffff" />
                <circle cx="420" cy="320" r="10" fill="#ffffff" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Form Section */}
        <div className="auth-form-container">
          <h2>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Create Account
          </h2>
          
          {(error || errorMessage) && (
            <div className="error-message">
              {error || errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                required
              >
                <option value="patient">Patient</option>
                <option value="provider">Provider</option>
                <option value="payer">Payer</option>
              </select>
            </div>
            
            <button type="submit" className="auth-loginbtn" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
          
          <div className="auth-links">
            <p>
              Already have an account? <Link to="/login">Login here</Link>
            </p>
          </div>
          
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <Link to="/" style={{ color: '#4299e1', textDecoration: 'none', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;